import type { Result, Search, SearchWithExtras } from 'generated/api/models'
import { getSearchClient } from 'services/FionaAPI/clients'
import { FionaAPIError } from 'services/FionaAPI/templates/errors'
import { SearchLogger as Logger } from 'services/Logger'
import { DataWrapper } from 'components/patterns/suspense/internals/types'
import { createDataWrapper } from 'components/patterns/suspense/internals/DataWrapper'
import { ensureDate } from '../../../lib/undefinedHelpers/dates'
import { ensureArray, SometimesArray } from '../../../lib/types/arrays'

const CREATED_DESC = 'created DESC'

const constructSearchDates = <T extends Search>(searches: SometimesArray<T>): Array<T> =>
	ensureArray(searches).map(search => ({
		...search,
		created: ensureDate(search.created)
	}))

export const getResults = async (searchId: string): Promise<Result> => {
	Logger.debug(`Search Results Requested: ${searchId}`)
	return await getSearchClient().getResults({ id: searchId })
}

export const getSearches = async (limit = 10, skip = 0): Promise<SearchWithExtras[]> => {
	return await getSearchClient()
		.getAllSearches({
			filter: { where: { executed: { eq: true } }, order: [CREATED_DESC], limit: limit, skip: skip },
			views: ['summary']
		})
		.then(constructSearchDates)
}

export const getMostRecentUnexecutedSearches = async (): Promise<SearchWithExtras | null> => {
	const result = await getSearchClient()
		.getAllSearches({
			views: ['summary'],
			filter: { limit: 1, order: [CREATED_DESC], where: { productTypes: ['loan'], executed: { eq: false } } }
		})
		.then(constructSearchDates)
	if (result) {
		const val = result.shift()
		if (val) return val
	}
	return null
}

export const getMostRecentLoanSearch = async (): Promise<SearchWithExtras> => {
	const result = await getSearchClient()
		.getAllSearches({
			views: ['summary'],
			filter: { limit: 1, order: [CREATED_DESC], where: { productTypes: ['loan'] } }
		})
		.then(constructSearchDates)
	if (result) {
		try {
			const val = result.pop()
			if (val) return val
		} catch (err) {
			Logger.error(err)
		}
	}
	throw new FionaAPIError('No Recent Searches')
}

export const fetchPendingSearch = (): DataWrapper<SearchWithExtras> => {
	const promise = getMostRecentUnexecutedSearches()
	return createDataWrapper(async () => {
		return await promise
	})
}

export const fetchRecentSearches = (): Array<DataWrapper<SearchWithExtras>> => {
	const promise = getSearches(2).then(constructSearchDates)
	return [0, 1].map(id =>
		createDataWrapper(async () => {
			const value = await promise
			return value[id]
		})
	)
}
